import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import SuperAdminLayout from "@/components/admin/super-admin-layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Building,
  Users,
  CreditCard,
  PlusCircle,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  DollarSign,
  Activity,
  AlertTriangle,
  Shield,
  BarChart3,
  Mail,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import RevenueForecastTable from "@/components/admin/revenue-forecast-table";

interface DashboardStats {
  totalTenants: number;
  activeTenants: number;
  inactiveTenants: number;
  totalUsers: number;
  activeUsers: number;
  totalLicenses: number;
  activeLicenses: number;
  totalLicensesAdded: number;
  licensesRenewedOnTime: number;
  totalMonthlyCost: number;
  totalApplications: number;
  mrr: number;
  mrrChange: number;
  alertsCount: number;
  freeTenants: number;
  planDistribution?: Record<string, number>;
  upcomingRenewals?: Array<{
    id: number;
    tenant: string;
    plan: string;
    renewalDate: string;
    amount: number;
    status: string;
  }>;
  systemStatus: {
    status: 'healthy' | 'warning' | 'critical';
    uptime: number;
    memory: number;
    cpu: number;
  };
}

// Summary Card component - styled like the tenant dashboard's SummaryCards component
const SummaryCard = ({ title, value, icon, iconColor, change }: { 
  title: string;
  value: string | number;
  icon: React.ReactNode;
  iconColor: string;
  change?: { value: number; isPositive: boolean; label: string };
}) => (
  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 transition duration-150 hover:shadow-md">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-slate-500">{title}</p>
        <h3 className="text-2xl font-bold text-slate-800 mt-1">{value}</h3>
      </div>
      <div className={`w-12 h-12 ${iconColor} rounded-full flex items-center justify-center`}>
        {icon}
      </div>
    </div>
    {change && (
      <div className="mt-3 flex items-center text-xs font-medium">
        <span className={`flex items-center ${change.isPositive ? 'text-emerald-600' : 'text-rose-600'}`}>
          {change.isPositive ? <ArrowUp className="w-3 h-3 mr-1" /> : <ArrowDown className="w-3 h-3 mr-1" />}
          {Math.abs(change.value)}%
        </span>
        <span className="text-slate-500 ml-2">{change.label}</span>
      </div>
    )}
  </div>
);

// Alert component for system issues
const SystemAlert = ({ title, description, severity }: {
  title: string;
  description: string;
  severity: 'warning' | 'critical' | 'info';
}) => {
  const bgColor = severity === 'critical' ? 'bg-red-50 border-red-200' : 
                 severity === 'warning' ? 'bg-amber-50 border-amber-200' : 
                 'bg-blue-50 border-blue-200';
  
  const textColor = severity === 'critical' ? 'text-red-800' : 
                   severity === 'warning' ? 'text-amber-800' : 
                   'text-blue-800';
  
  const iconColor = severity === 'critical' ? 'text-red-500' : 
                   severity === 'warning' ? 'text-amber-500' : 
                   'text-blue-500';
  
  return (
    <div className={`flex items-start p-3 ${bgColor} border rounded-md`}>
      <AlertTriangle className={`h-5 w-5 ${iconColor} mr-3 mt-0.5`} />
      <div>
        <h4 className={`font-medium ${textColor}`}>{title}</h4>
        <p className={`text-sm ${severity === 'critical' ? 'text-red-700' : severity === 'warning' ? 'text-amber-700' : 'text-blue-700'}`}>
          {description}
        </p>
      </div>
    </div>
  );
};

// Activity Item component for system activities
const ActivityItem = ({ title, time, icon }: {
  title: string;
  time: string;
  icon: React.ReactNode;
}) => (
  <div className="flex items-center p-3 border-b last:border-0">
    <div className="rounded-full p-2 bg-slate-100 mr-3">
      {icon}
    </div>
    <div className="flex-1">
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-slate-500">{time}</p>
    </div>
  </div>
);

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [clearingData, setClearingData] = useState(false);

  // Get dashboard stats - using correct API endpoint
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["/api/admin/system/dashboard-stats"],
    queryFn: async () => {
      try {
        const response = await apiRequest("/api/admin/system/dashboard-stats", {
          method: "GET",
        });
        console.log("Dashboard stats response:", response);
        return response;
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
        throw error;
      }
    },
  });

  // Debug logging to see actual data structure
  console.log('Dashboard stats received:', stats);
  
  // Get system activity
  interface SystemActivity {
    id: number;
    title: string;
    time: string;
    timestamp: string;
    type: string;
  }
  
  const { data: activities, isLoading: activitiesLoading } = useQuery({
    queryKey: ["/api/admin/system/activity"],
    queryFn: async () => {
      try {
        const response = await apiRequest("/api/admin/system/activity", {
          method: "GET",
        });
        return response;
      } catch (error) {
        console.error("Error fetching system activity:", error);
        throw error;
      }
    },
  });
  
  // Get system alerts
  interface SystemAlert {
    id: number;
    severity: 'info' | 'warning' | 'error';
    message: string;
    timestamp: string;
    acknowledged: boolean;
  }
  
  const { data: alerts, isLoading: alertsLoading } = useQuery({
    queryKey: ["/api/admin/alerts"],
    queryFn: async () => {
      try {
        const response = await apiRequest("/api/admin/system/alerts", {
          method: "GET",
        });
        return response;
      } catch (error) {
        console.error("Error fetching system alerts:", error);
        throw error;
      }
    },
  });

  // Function to clear test data for demonstration
  const clearTestData = async () => {
    if (confirm("Are you sure you want to clear all test data? This will remove activity logs and test tenants.")) {
      setClearingData(true);
      try {
        await apiRequest("/api/admin/system/clear-test-data", {
          method: "POST",
        });
        
        // Invalidate all relevant queries to refresh data
        queryClient.invalidateQueries({ queryKey: ["/api/admin/system/activity"] });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/alerts"] });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/system/dashboard-stats"] });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/system/tenant-summaries"] });
        
        alert("Test data cleared successfully. You can now test registering a new user to see it reflected in the dashboard.");
      } catch (error) {
        console.error("Error clearing test data:", error);
        alert("Failed to clear test data. See console for details.");
      } finally {
        setClearingData(false);
      }
    }
  };
  
  // Check if any data is still loading
  const isLoading = statsLoading || activitiesLoading || alertsLoading;
  
  if (isLoading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
        </div>
      </SuperAdminLayout>
    );
  }

  // Format large numbers with comma separators
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  // Helper to format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Status indicator color
  const getStatusColor = (status: 'healthy' | 'warning' | 'critical') => {
    switch (status) {
      case 'healthy': return 'text-emerald-500';
      case 'warning': return 'text-amber-500';
      case 'critical': return 'text-rose-500';
      default: return 'text-emerald-500';
    }
  };

  return (
    <SuperAdminLayout>
      <div>
        {/* Dashboard Header */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-4 md:mb-0">
              <h2 className="text-2xl font-bold text-slate-800">Admin Dashboard</h2>
              <p className="text-sm text-slate-500 mt-1">Platform-wide overview and management</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                size="sm" 
                className="bg-blue-600 hover:bg-blue-700" 
                onClick={() => window.location.href = "/admin/email"}
              >
                <Mail className="h-4 w-4 mr-2" />
                Send Announcement
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => window.location.href = "/admin/reports"}
              >
                <FileText className="h-4 w-4 mr-2" />
                Reports
              </Button>
            </div>
          </div>
        </div>
        
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <SummaryCard
            title="Total Organizations"
            value={formatNumber(stats?.totalTenants || 0)}
            icon={<Building className="h-5 w-5" />}
            iconColor="bg-blue-100 text-blue-600"
          />
          
          <SummaryCard
            title="Active Users"
            value={formatNumber(stats?.activeUsers || 0)}
            icon={<Users className="h-5 w-5" />}
            iconColor="bg-purple-100 text-purple-600"
          />
          
          <SummaryCard
            title="Active Licenses"
            value={formatNumber(stats?.activeLicenses || 0)}
            icon={<CreditCard className="h-5 w-5" />}
            iconColor="bg-emerald-100 text-emerald-600"
          />
          
          <SummaryCard
            title="Monthly Revenue"
            value={formatCurrency(stats?.mrr || 0)}
            icon={<DollarSign className="h-5 w-5" />}
            iconColor="bg-amber-100 text-amber-600"
          />
        </div>
        

        
        {/* Tenant Management Dashboard - Full width */}
        <div className="mb-6">
          <Card className="bg-white rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition duration-150">
            <CardHeader>
              <CardTitle className="text-lg font-medium">Tenant Management Dashboard</CardTitle>
              <CardDescription>Subscription overview and tenant health metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Plan Distribution Chart */}
                <div className="border rounded-lg p-4">
                  <h3 className="text-sm font-medium mb-4">Plan Distribution</h3>
                  <div className="space-y-4">
                    {/* Only show the real subscription plans from Stripe */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm">Single</span>
                        <span className="text-sm font-medium">
                          {stats?.planDistribution?.Single || 0} tenants
                        </span>
                      </div>
                      <Progress 
                        value={
                          stats?.totalTenants && stats?.planDistribution?.Single 
                            ? (stats.planDistribution.Single / stats.totalTenants) * 100 
                            : 0
                        } 
                        className="h-2 bg-slate-100" 
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm">Team</span>
                        <span className="text-sm font-medium">
                          {stats?.planDistribution?.Team || 0} tenants
                        </span>
                      </div>
                      <Progress 
                        value={
                          stats?.totalTenants && stats?.planDistribution?.Team 
                            ? (stats.planDistribution.Team / stats.totalTenants) * 100 
                            : 0
                        } 
                        className="h-2 bg-slate-100" 
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm">Enterprise</span>
                        <span className="text-sm font-medium">
                          {stats?.planDistribution?.Enterprise || 0} tenants
                        </span>
                      </div>
                      <Progress 
                        value={
                          stats?.totalTenants && stats?.planDistribution?.Enterprise 
                            ? (stats.planDistribution.Enterprise / stats.totalTenants) * 100 
                            : 0
                        } 
                        className="h-2 bg-slate-100" 
                      />
                    </div>
                  </div>
                </div>

                {/* Tenant Health Metrics */}
                <div className="border rounded-lg p-4">
                  <h3 className="text-sm font-medium mb-4">Tenant Health Metrics</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Active Tenants</span>
                      <span className="text-sm font-medium text-emerald-600">
                        {formatNumber(stats?.activeTenants || 0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Inactive Tenants</span>
                      <span className="text-sm font-medium text-amber-600">
                        {formatNumber(stats?.inactiveTenants || 0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Total Licenses Added</span>
                      <span className="text-sm font-medium text-blue-600">
                        {formatNumber(stats?.totalLicensesAdded || 0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">License Renewals On Time</span>
                      <span className="text-sm font-medium text-emerald-600">
                        {formatNumber(stats?.licensesRenewedOnTime || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        

        
        {/* System Alerts */}
        {alerts && alerts.length > 0 && (
          <div className="mb-6">
            <Card className="bg-white rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition duration-150">
              <CardHeader>
                <CardTitle className="text-lg font-medium flex items-center">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mr-2" />
                  System Alerts
                </CardTitle>
                <CardDescription>
                  {alerts.length} issue{alerts.length !== 1 ? 's' : ''} requiring attention
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {alerts.map(alert => (
                  <SystemAlert
                    key={alert.id}
                    title={alert.severity === 'error' ? 'Critical Issue' : 
                           alert.severity === 'warning' ? 'Warning' : 'Information'}
                    description={alert.message}
                    severity={alert.severity === 'error' ? 'critical' : 
                              alert.severity === 'warning' ? 'warning' : 'info'}
                  />
                ))}
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Revenue Forecast Table for Cash Flow Planning */}
        <div className="mb-6">
          <RevenueForecastTable />
        </div>

        {/* Recent System Activity - Bottom position */}
        <div className="mb-6">
          <Card className="bg-white rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition duration-150">
            <CardHeader>
              <CardTitle className="text-lg font-medium">Recent System Activity</CardTitle>
              <CardDescription>Latest system events and actions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {!activities || activities.length === 0 ? (
                  <div className="text-center py-4 text-sm text-slate-500">No recent activity</div>
                ) : (
                  activities.slice(0, 5).map((activity) => (
                    <div 
                      key={activity.id} 
                      className="flex items-start px-4 py-3 rounded-md border border-slate-100 hover:bg-slate-50"
                    >
                      <div className="flex-shrink-0 mr-4 mt-1 p-1.5 rounded-full bg-slate-100">
                        {activity.type === 'tenant_created' || activity.type === 'tenant_verified' ? (
                          <Building className="h-4 w-4 text-blue-500" />
                        ) : activity.type === 'user_created' || activity.type === 'user_updated' ? (
                          <Users className="h-4 w-4 text-purple-500" />
                        ) : activity.type === 'subscription_created' || activity.type === 'payment_processed' ? (
                          <CreditCard className="h-4 w-4 text-emerald-500" />
                        ) : activity.type === 'login_failed' ? (
                          <Shield className="h-4 w-4 text-rose-500" />
                        ) : activity.type === 'login_success' ? (
                          <Shield className="h-4 w-4 text-emerald-500" />
                        ) : (
                          <Activity className="h-4 w-4 text-slate-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{activity.title}</p>
                        <p className="text-xs text-slate-500">{activity.time}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              <div className="flex justify-end mt-4">
                <Button 
                  onClick={() => window.location.href = "/admin/activity"} 
                  variant="outline" 
                  size="sm"
                >
                  View All Activity
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

      </div>
    </SuperAdminLayout>
  );
};

export default AdminDashboard;